import { useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/molecules/Modal';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useAllAssignees } from '@/hooks/useTickets';
import { DURATA_DEFAULT, DURATA_OPTIONS, normalizeDurata } from '@/lib/appuntamento';
import { canBookAppointment, leadLabel } from '@/lib/lead';
import { queryKeys } from '@/lib/queryKeys';
import { clientiService } from '@/services/clientiService';
import { leadService } from '@/services/leadService';
import { leadStatoMeta, ruoloStatus } from '@/lib/statusMaps';
import { utentiService } from '@/services/utentiService';
import type { Cliente, Lead, UUID } from '@/types';

type Collegamento = 'operatori' | 'lead' | 'cliente';

interface AziendaScelta {
  id: UUID;
  ragione_sociale: string;
  email_principale: string | null;
}

function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function defaultWhen(): string {
  const date = new Date();
  date.setMinutes(date.getMinutes() + 60);
  const remainder = date.getMinutes() % 15;
  if (remainder !== 0) date.setMinutes(date.getMinutes() + (15 - remainder));
  date.setSeconds(0, 0);
  return toLocalInput(date.toISOString());
}

export interface AppuntamentoFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** ISO datetime taken from the calendar slot. Empty uses the next rounded hour. */
  initialWhen?: string | null;
}

export function AppuntamentoFormDialog({ open, onClose, initialWhen = null }: AppuntamentoFormDialogProps): JSX.Element {
  const { user } = useAuth();
  const { isStaff, isPartnerAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const assignees = useAllAssignees(open && !isStaff);
  const directory = useQuery({
    queryKey: queryKeys.utenti.list({ attivo: true, pageSize: 200, sortBy: 'nome', sortDir: 'asc' }),
    queryFn: () => utentiService.list({ attivo: true, pageSize: 200, sortBy: 'nome', sortDir: 'asc' }),
    enabled: open && isStaff,
  });
  const [collegamento, setCollegamento] = useState<Collegamento>('operatori');
  const [titolo, setTitolo] = useState('');
  const [leadQuery, setLeadQuery] = useState('');
  const [clienteQuery, setClienteQuery] = useState('');
  const [debouncedLead, setDebouncedLead] = useState('');
  const [debouncedCliente, setDebouncedCliente] = useState('');
  const [selected, setSelected] = useState<Lead | null>(null);
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [quando, setQuando] = useState(() => toLocalInput(initialWhen) || defaultWhen());
  const [durata, setDurata] = useState(DURATA_DEFAULT);
  const [partecipanti, setPartecipanti] = useState<UUID[]>(() => (isStaff && user?.id ? [user.id] : []));
  const [aziendaId, setAziendaId] = useState('');
  const [invitaReferente, setInvitaReferente] = useState(true);
  const [titoloError, setTitoloError] = useState('');
  const [leadError, setLeadError] = useState('');
  const [clienteError, setClienteError] = useState('');
  const [quandoError, setQuandoError] = useState('');
  const [partecipantiError, setPartecipantiError] = useState('');
  const [formError, setFormError] = useState('');
  const seeded = useRef<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedLead(leadQuery.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [leadQuery]);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedCliente(clienteQuery.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [clienteQuery]);

  const leads = useQuery({
    queryKey: queryKeys.lead.list({ search: debouncedLead, pageSize: 20 }),
    queryFn: () => leadService.list({ search: debouncedLead || undefined, pageSize: 20 }),
    enabled: open && collegamento === 'lead',
  });
  const clienti = useQuery({
    queryKey: queryKeys.clienti.list({ search: debouncedCliente, pageSize: 20 }),
    queryFn: () => clientiService.list({ search: debouncedCliente || undefined, pageSize: 20 }),
    enabled: open && collegamento === 'cliente',
  });
  const detail = useQuery({
    queryKey: queryKeys.lead.detail(selected?.id ?? ''),
    queryFn: () => leadService.get(selected?.id as UUID),
    enabled: open && collegamento === 'lead' && Boolean(selected?.id),
  });
  const ownCliente = useQuery({
    queryKey: queryKeys.clienti.detail(user?.cliente_id ?? ''),
    queryFn: () => clientiService.get(user?.cliente_id as UUID),
    enabled: open && !isStaff && Boolean(user?.cliente_id),
  });
  const finali = useQuery({
    queryKey: queryKeys.clienti.clientiFinali(user?.cliente_id ?? ''),
    queryFn: () => clientiService.listClientiFinali(user?.cliente_id as UUID),
    enabled: open && isPartnerAdmin && Boolean(user?.cliente_id),
  });

  const save = useMutation({
    mutationFn: leadService.createAppuntamento,
    meta: { successMessage: 'Appuntamento fissato.', silentError: true },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.lead.all });
    },
  });

  useEffect(() => {
    if (collegamento !== 'lead' || !selected || detail.data?.id !== selected.id) return;
    if (seeded.current === selected.id) return;
    seeded.current = selected.id;
    const appointment = detail.data.appuntamento;
    if (!appointment) {
      setInvitaReferente(Boolean(selected.referente_email));
      return;
    }
    setQuando(toLocalInput(appointment.inizia_at));
    setDurata(normalizeDurata(appointment.durata_minuti));
    const ids = appointment.partecipanti.flatMap((person) => (person.utente_id ? [person.utente_id] : []));
    if (ids.length > 0) setPartecipanti(ids);
    setInvitaReferente(appointment.partecipanti.some((person) => !person.utente_id));
  }, [collegamento, selected, detail.data]);

  const leadOptions = useMemo(() => {
    const rows = leads.data?.data ?? [];
    if (!selected || rows.some((row) => row.id === selected.id)) return rows;
    return [selected, ...rows];
  }, [leads.data, selected]);
  const clienteOptions = useMemo(() => {
    const rows = clienti.data?.data ?? [];
    if (!cliente || rows.some((row) => row.id === cliente.id)) return rows;
    return [cliente, ...rows];
  }, [clienti.data, cliente]);
  const aziende = useMemo(() => {
    if (isStaff) return [];
    const rows: AziendaScelta[] = [];
    if (user?.cliente_id) {
      rows.push({
        id: user.cliente_id,
        ragione_sociale: ownCliente.data?.ragione_sociale ?? user.cliente_ragione_sociale ?? 'La tua azienda',
        email_principale: ownCliente.data?.email_principale ?? null,
      });
    }
    if (isPartnerAdmin) {
      for (const link of finali.data ?? []) {
        const finale = link.cliente_finale;
        if (!finale || rows.some((row) => row.id === finale.id)) continue;
        if (finale.stato === 'perso' || finale.stato === 'cessato') continue;
        rows.push({
          id: finale.id,
          ragione_sociale: finale.ragione_sociale,
          email_principale: finale.email_principale,
        });
      }
    }
    return rows;
  }, [finali.data, isPartnerAdmin, isStaff, ownCliente.data, user]);
  const azienda = aziende.find((row) => row.id === aziendaId) ?? aziende[0] ?? null;
  useEffect(() => {
    if (!open || isStaff || aziendaId || !aziende[0]) return;
    setAziendaId(aziende[0].id);
  }, [aziendaId, aziende, isStaff, open]);
  const people = isStaff
    ? (directory.data?.data ?? []).map((person) => ({ id: person.id, nome: person.nome, ruolo: person.ruolo }))
    : (assignees.data ?? []).map((person) => ({ id: person.id, nome: person.nome, ruolo: person.ruolo }));
  const peopleLoading = isStaff ? directory.isLoading : assignees.isLoading;
  const selectedPeople = people.filter((person) => partecipanti.includes(person.id));
  const bookable = selected ? canBookAppointment(selected) : false;
  const guestEmail = isStaff
    ? collegamento === 'lead'
      ? selected?.referente_email
      : collegamento === 'cliente'
        ? cliente?.email_principale
        : null
    : (azienda?.email_principale ?? null);
  const guestName = isStaff
    ? collegamento === 'lead'
      ? selected?.referente_nome?.trim() || 'il referente'
      : collegamento === 'cliente'
        ? cliente?.ragione_sociale?.trim() || 'il cliente'
        : ''
    : azienda?.ragione_sociale?.trim() || 'il cliente';

  return (
    <Modal
      open={open}
      onClose={save.isPending ? () => undefined : onClose}
      maxWidth="sm"
      disableClose={save.isPending}
      onSubmit={(event) => {
        event.preventDefault();
        if (!isStaff) {
          if (!azienda) {
            setClienteError('Scegli il cliente.');
            return;
          }
          if (!quando) {
            setQuandoError('Indica data e ora.');
            return;
          }
          if (partecipanti.length === 0) {
            setPartecipantiError('Scegli almeno un collaboratore.');
            return;
          }
          const at = new Date(quando);
          if (Number.isNaN(at.getTime())) {
            setQuandoError('Data non valida.');
            return;
          }
          save.mutate(
            {
              inizia_at: at.toISOString(),
              durata_minuti: durata,
              cliente_id: azienda.id,
              partecipanti_ids: partecipanti,
              invita_referente: invitaReferente,
            },
            {
              onSuccess: () => onClose(),
              onError: (error) => setFormError(error instanceof Error ? error.message : 'Non è stato possibile fissare l’appuntamento.'),
            },
          );
          return;
        }
        if (collegamento === 'operatori' && !titolo.trim()) {
          setTitoloError('Indica un titolo.');
          return;
        }
        if (collegamento === 'lead' && !selected) {
          setLeadError('Scegli il lead.');
          return;
        }
        if (collegamento === 'lead' && selected && !bookable) {
          setLeadError('Questo lead non può prendere un appuntamento.');
          return;
        }
        if (collegamento === 'cliente' && !cliente) {
          setClienteError('Scegli il cliente.');
          return;
        }
        if (!quando) {
          setQuandoError('Indica data e ora.');
          return;
        }
        if (partecipanti.length === 0) {
          setPartecipantiError('Scegli almeno un collaboratore.');
          return;
        }
        const at = new Date(quando);
        if (Number.isNaN(at.getTime())) {
          setQuandoError('Data non valida.');
          return;
        }
        save.mutate(
          {
            inizia_at: at.toISOString(),
            durata_minuti: durata,
            titolo: collegamento === 'operatori' ? titolo.trim() : undefined,
            lead_id: collegamento === 'lead' ? selected?.id : null,
            cliente_id: collegamento === 'cliente' ? cliente?.id : null,
            partecipanti_ids: partecipanti,
            invita_referente: collegamento === 'operatori' ? false : invitaReferente,
          },
          {
            onSuccess: () => onClose(),
            onError: (error) => setFormError(error instanceof Error ? error.message : 'Non è stato possibile fissare l’appuntamento.'),
          },
        );
      }}
    >
      <Modal.Header title="Nuovo appuntamento" />
      <Modal.Body>
        <Stack spacing={2}>
          {isStaff ? (
          <TextField
            select
            label="Collegamento"
            value={collegamento}
            disabled={save.isPending}
            onChange={(event) => {
              setCollegamento(event.target.value as Collegamento);
              setTitoloError('');
              setLeadError('');
              setClienteError('');
              setFormError('');
            }}
            helperText="Solo tra operatori, oppure collegato a un lead o a un cliente."
          >
            <MenuItem value="operatori">Solo operatori</MenuItem>
            <MenuItem value="lead">Lead</MenuItem>
            <MenuItem value="cliente">Cliente</MenuItem>
          </TextField>
          ) : aziende.length > 1 ? (
            <TextField
              select
              label="Cliente"
              value={azienda?.id ?? ''}
              disabled={save.isPending}
              onChange={(event) => {
                setAziendaId(event.target.value);
                setClienteError('');
                const next = aziende.find((row) => row.id === event.target.value);
                setInvitaReferente(Boolean(next?.email_principale));
              }}
              error={Boolean(clienteError)}
              helperText={clienteError || 'La tua azienda o un cliente finale. Lo vedono gli utenti di quel cliente.'}
            >
              {aziende.map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {row.ragione_sociale}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              label="Cliente"
              value={azienda?.ragione_sociale ?? ''}
              disabled
              error={Boolean(clienteError)}
              helperText={
                clienteError ||
                (isPartnerAdmin
                  ? 'La tua azienda. I clienti finali compariranno qui quando ne colleghi uno.'
                  : 'Visibile a tutti gli utenti della tua azienda.')
              }
            />
          )}
          {isStaff && collegamento === 'operatori' ? (
            <TextField
              label="Titolo"
              value={titolo}
              required
              disabled={save.isPending}
              onChange={(event) => {
                setTitolo(event.target.value);
                setTitoloError('');
              }}
              error={Boolean(titoloError)}
              helperText={titoloError || 'Compare sul calendario, per esempio una riunione interna.'}
            />
          ) : null}
          {isStaff && collegamento === 'lead' ? (
            <Autocomplete
              options={leadOptions}
              value={selected}
              loading={leads.isLoading}
              disabled={save.isPending}
              getOptionLabel={(option) => leadLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionDisabled={(option) => !canBookAppointment(option)}
              onChange={(_event, next) => {
                setSelected(next);
                setLeadError('');
                setFormError('');
                seeded.current = null;
                if (next) setInvitaReferente(Boolean(next.referente_email));
              }}
              onInputChange={(_event, value, reason) => {
                if (reason === 'input') setLeadQuery(value);
              }}
              noOptionsText={leads.isLoading ? 'Caricamento…' : 'Nessun lead'}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography variant="body2">{leadLabel(option)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {leadStatoMeta[option.stato].label}
                      {option.referente_email ? ` · ${option.referente_email}` : ''}
                    </Typography>
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lead"
                  required
                  inputProps={{ ...params.inputProps, required: false }}
                  placeholder="Cerca per azienda, referente o email"
                  error={Boolean(leadError)}
                  helperText={leadError || 'Il lead passa ad appuntamento fissato.'}
                />
              )}
            />
          ) : null}
          {isStaff && collegamento === 'cliente' ? (
            <Autocomplete
              options={clienteOptions}
              value={cliente}
              loading={clienti.isLoading}
              disabled={save.isPending}
              getOptionLabel={(option) => option.ragione_sociale}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, next) => {
                setCliente(next);
                setClienteError('');
                setFormError('');
                setInvitaReferente(Boolean(next?.email_principale));
              }}
              onInputChange={(_event, value, reason) => {
                if (reason === 'input') setClienteQuery(value);
              }}
              noOptionsText={clienti.isLoading ? 'Caricamento…' : 'Nessun cliente'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente"
                  required
                  inputProps={{ ...params.inputProps, required: false }}
                  placeholder="Cerca per ragione sociale o email"
                  error={Boolean(clienteError)}
                  helperText={clienteError || 'L’appuntamento resta visibile anche se il cliente cambia stato.'}
                />
              )}
            />
          ) : null}
          <TextField
            type="datetime-local"
            label="Quando"
            value={quando}
            onChange={(event) => {
              setQuando(event.target.value);
              setQuandoError('');
            }}
            required
            fullWidth
            disabled={save.isPending}
            InputLabelProps={{ shrink: true }}
            error={Boolean(quandoError)}
            helperText={quandoError || 'Ogni collaboratore riceve una notifica e una mail 30 minuti prima e un’altra 1 minuto prima.'}
          />
          <TextField
            select
            label="Durata"
            value={durata}
            disabled={save.isPending}
            onChange={(event) => setDurata(normalizeDurata(Number(event.target.value)))}
          >
            {DURATA_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            multiple
            options={people}
            value={selectedPeople}
            loading={peopleLoading}
            disabled={save.isPending}
            getOptionLabel={(option) => option.nome}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterSelectedOptions
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack>
                  <span>{option.nome}</span>
                  <Typography variant="caption" color="text.secondary">
                    {ruoloStatus(option.ruolo).label}
                  </Typography>
                </Stack>
              </li>
            )}
            onChange={(_event, next) => {
              setPartecipanti(next.map((option) => option.id));
              setPartecipantiError('');
            }}
            noOptionsText={peopleLoading ? 'Caricamento…' : 'Nessun collaboratore'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Collaboratori"
                required
                inputProps={{ ...params.inputProps, required: false }}
                error={Boolean(partecipantiError)}
                helperText={
                  partecipantiError ||
                  (isStaff
                    ? 'Puoi invitare amministratori, operatori, utenti e admin cliente.'
                    : 'Operatori Wenkey che vedono l’appuntamento e ricevono i promemoria.')
                }
              />
            )}
          />
          {guestEmail ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={invitaReferente}
                  onChange={(event) => setInvitaReferente(event.target.checked)}
                  disabled={save.isPending}
                />
              }
              label={`Invita anche ${guestName} (${guestEmail})`}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {!isStaff
                ? 'Il cliente non ha un’email, quindi non riceve la mail.'
                : collegamento === 'operatori'
                  ? 'Nessun esterno: i promemoria arrivano solo ai collaboratori.'
                  : collegamento === 'cliente' && cliente
                    ? 'Il cliente non ha un’email, quindi non riceve la mail.'
                    : selected
                      ? 'Il referente non ha un indirizzo email, quindi non riceve la mail.'
                      : 'Se colleghi un lead o un cliente con email, puoi invitarlo.'}
            </Typography>
          )}
          {formError ? (
            <Typography variant="body2" color="error">
              {formError}
            </Typography>
          ) : null}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} disabled={save.isPending}>
          Annulla
        </Button>
        <Button type="submit" variant="contained" disabled={save.isPending || (collegamento === 'lead' && Boolean(selected) && !bookable)}>
          Fissa appuntamento
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
